import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Checkbox, Label, Spinner, TextInput } from "flowbite-react";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiOutlineExternalLink,
  HiOutlineRefresh,
} from "react-icons/hi";
import {
  AccountingConnection,
  AccountingBackgroundSyncStatus,
  AccountingIntegrationCatalogItem,
  configureTripletexWebhooks,
  connectTripletex,
  disconnectAccountingConnection,
  getAccountingIntegrationCatalog,
  getMcpTokens,
  testAccountingConnection,
} from "../../../api";
import { Button, Dialog, InfoMessageAtom, StatusBadge } from "@components";
import { cn, inputSurface } from "@design-system";
import { useUserSession } from "../../../context/UserContext";
import { useWorkspace } from "../../../context/WorkspaceContext";
import useCompanies from "../../../hooks/useCompanies";
import {
  IntegrationDefinition,
  integrationCatalog,
} from "../../../integrations/catalog";

const TRIPLETEX_TOKEN_HELP_URL =
  "https://developer.tripletex.no/docs/documentation/authentication-and-tokens/";

const getErrorMessage = (error: any, fallback: string): string => {
  const responseError = error?.response?.data?.error;
  if (typeof responseError === "string" && responseError.trim()) {
    return responseError;
  }
  return fallback;
};

const formatDateTime = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const BackgroundSyncStatus: FC<{
  status?: AccountingBackgroundSyncStatus | null;
  timestamp?: string | null;
}> = ({ status, timestamp }) => {
  const { t } = useTranslation("workspace");
  const presentations = {
    succeeded: {
      label: t("integrations.tripletex.sync_succeeded"),
      icon: <HiCheckCircle className="h-5 w-5 text-lgb-green-400" />,
    },
    partial: {
      label: t("integrations.tripletex.sync_partial"),
      icon: <HiExclamationCircle className="h-5 w-5 text-yellow-500" />,
    },
    failed: {
      label: t("integrations.tripletex.sync_failed"),
      icon: <HiExclamationCircle className="h-5 w-5 text-red-500" />,
    },
  };
  const presentation = status ? presentations[status] : null;
  const formattedTimestamp = formatDateTime(timestamp);

  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-2">
      {presentation?.icon}
      {presentation ? <span>{presentation.label}</span> : null}
      {formattedTimestamp ? (
        <span className="font-normal text-secondary">
          {presentation ? "· " : ""}
          {formattedTimestamp}
        </span>
      ) : null}
    </span>
  );
};

const ConnectionStatus: FC<{
  status?: AccountingConnection["status"];
}> = ({ status }) => {
  const { t } = useTranslation("workspace");
  const presentation = {
    active: {
      label: t("integrations.status.connected"),
      bg: "bg-lgb-green-100 text-lgb-green-600 dark:bg-lgb-green-700/20 dark:text-lgb-green-300",
    },
    reauthorization_required: {
      label: t("integrations.status.reconnect"),
      bg: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    },
    error: {
      label: t("integrations.status.error"),
      bg: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    },
    disconnected: {
      label: t("integrations.status.not_connected"),
      bg: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    },
  }[status || "disconnected"];

  return (
    <StatusBadge
      status={presentation.label}
      bgStyle={presentation.bg}
      rounded
    />
  );
};

const IntegrationMark: FC<{ integration: IntegrationDefinition }> = ({
  integration,
}) => {
  const asset =
    integration.cardAsset === "emblem" && integration.emblemSrc
      ? integration.emblemSrc
      : integration.logoSrc;

  return (
    <img
      src={asset}
      alt=""
      aria-hidden="true"
      className="h-12 w-12 shrink-0 rounded-[8px] object-contain"
    />
  );
};

interface IntegrationRowProps {
  label: string;
  value: React.ReactNode;
}

const IntegrationRow: FC<IntegrationRowProps> = ({ label, value }) => (
  <div className="grid gap-1 border-t border-border py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-6">
    <span className="text-sm text-secondary">{label}</span>
    <div className="min-w-0 text-sm font-medium text-text sm:text-right">
      {value}
    </div>
  </div>
);

interface TripletexConnectDialogProps {
  show: boolean;
  companyName: string;
  isReconnect: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  authorizationVersion: string;
  onClose: () => void;
  onConnect: (employeeToken: string, authorizationVersion: string) => void;
}

export const TripletexConnectDialog: FC<TripletexConnectDialogProps> = ({
  show,
  companyName,
  isReconnect,
  isSubmitting,
  errorMessage,
  authorizationVersion,
  onClose,
  onConnect,
}) => {
  const { t } = useTranslation(["common", "workspace"]);
  const employeeTokenInputRef = useRef<HTMLInputElement>(null);
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const tripletex = integrationCatalog.tripletex;

  useEffect(() => {
    if (!show) {
      setAuthorizationConfirmed(false);
    }
  }, [show]);

  const handleConfirm = () => {
    if (!authorizationConfirmed || !authorizationVersion) return;
    const employeeToken = employeeTokenInputRef.current?.value.trim() || "";
    if (!employeeToken) {
      employeeTokenInputRef.current?.focus();
      return;
    }
    onConnect(employeeToken, authorizationVersion);
  };

  return (
    <Dialog
      show={show}
      size="lg"
      title={t(
        isReconnect
          ? "workspace:integrations.tripletex.reconnect_title"
          : "workspace:integrations.tripletex.connect_title",
      )}
      toggleModal={onClose}
      isLoading={isSubmitting}
      addDisabled={!authorizationConfirmed || !authorizationVersion}
      buttonConfirmText={t(
        isReconnect
          ? "workspace:integrations.actions.reconnect"
          : "workspace:integrations.actions.connect",
      )}
      buttonCloseText={t("common:cancel")}
      confirmButton={handleConfirm}
      content={
        <div className="space-y-4">
          <img
            src={tripletex.logoSrc}
            alt={tripletex.name}
            className="h-8 w-auto"
          />
          <p className="text-sm leading-6 text-secondary">
            {t("workspace:integrations.tripletex.connect_description", {
              company: companyName,
            })}
          </p>
          <InfoMessageAtom
            variant="info"
            title={t("workspace:integrations.tripletex.token_help_title")}
          >
            <p>{t("workspace:integrations.tripletex.token_help")}</p>
            <a
              href={TRIPLETEX_TOKEN_HELP_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 font-medium text-brand hover:underline"
            >
              {t("workspace:integrations.tripletex.token_help_link")}
              <HiOutlineExternalLink className="h-4 w-4" />
            </a>
          </InfoMessageAtom>
          <div>
            <Label
              htmlFor="tripletex-employee-token"
              value={t("workspace:integrations.tripletex.employee_token")}
            />
            <TextInput
              id="tripletex-employee-token"
              ref={employeeTokenInputRef}
              type="password"
              autoComplete="off"
              placeholder={t(
                "workspace:integrations.tripletex.employee_token_placeholder",
              )}
              className="mt-1"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs leading-5 text-muted">
              {t("workspace:integrations.tripletex.token_security")}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
            <p className="text-sm font-medium text-text">
              {t("workspace:integrations.tripletex.authorization_title")}
            </p>
            <p
              id="tripletex-authorization-description"
              className="mt-1 text-sm leading-5 text-secondary"
            >
              {t("workspace:integrations.tripletex.authorization_scope")}
            </p>
            <div className="mt-3 flex items-start gap-3">
              <Checkbox
                id="tripletex-authorization-confirmed"
                checked={authorizationConfirmed}
                disabled={isSubmitting || !authorizationVersion}
                aria-describedby="tripletex-authorization-description"
                onChange={(event) =>
                  setAuthorizationConfirmed(event.target.checked)
                }
                className="mt-0.5 text-brand focus:ring-brand"
              />
              <Label
                htmlFor="tripletex-authorization-confirmed"
                value={t(
                  "workspace:integrations.tripletex.authorization_confirmation",
                  { company: companyName },
                )}
                className="cursor-pointer text-sm font-normal leading-5 text-text"
              />
            </div>
          </div>
          {errorMessage ? (
            <InfoMessageAtom variant="error">{errorMessage}</InfoMessageAtom>
          ) : null}
        </div>
      }
    />
  );
};

const IntegrationsSettingsContent: FC<{ detailMode?: boolean }> = ({
  detailMode = false,
}) => {
  const { t } = useTranslation(["common", "workspace"]);
  const [, setSearchParams] = useSearchParams();
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [tripletexIntegration, setTripletexIntegration] =
    useState<AccountingIntegrationCatalogItem | null>(null);
  const [activeMcpTokenCount, setActiveMcpTokenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState<string | null>(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [pageMessage, setPageMessage] = useState<{
    variant: "success" | "warning" | "error";
    text: string;
  } | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const catalogRequestId = useRef(0);

  const token = userSession?.token?.accessToken || "";
  const workspaceId = activeWorkspace?.workspaceId || "";
  const canManage =
    activeWorkspace?.role === "Owner" ||
    activeWorkspace?.role === "Administrator";

  useEffect(() => {
    if (
      companies.length > 0 &&
      !companies.some((company) => company.id === selectedCompanyId)
    ) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  const loadIntegration = useCallback(async () => {
    const requestId = ++catalogRequestId.current;
    if (!token || !workspaceId || !selectedCompanyId || !canManage) {
      setTripletexIntegration(null);
      setIsLoading(false);
      return;
    }
    setTripletexIntegration(null);
    setIsLoading(true);
    try {
      const catalog = await getAccountingIntegrationCatalog(
        token,
        workspaceId,
        selectedCompanyId,
      );
      if (requestId === catalogRequestId.current) {
        setTripletexIntegration(
          catalog.find((item) => item.providerKey === "tripletex") || null,
        );
      }
    } catch (error) {
      if (requestId === catalogRequestId.current) {
        setTripletexIntegration(null);
        setPageMessage({
          variant: "error",
          text: getErrorMessage(
            error,
            t("workspace:integrations.errors.load_connections"),
          ),
        });
      }
    } finally {
      if (requestId === catalogRequestId.current) {
        setIsLoading(false);
      }
    }
  }, [canManage, selectedCompanyId, t, token, workspaceId]);

  useEffect(() => {
    void loadIntegration();
  }, [loadIntegration]);

  useEffect(() => {
    if (!token || !workspaceId) return;
    getMcpTokens(token, workspaceId)
      .then((tokens) => {
        setActiveMcpTokenCount(tokens.filter((item) => !item.revokedAt).length);
      })
      .catch(() => setActiveMcpTokenCount(0));
  }, [token, workspaceId]);

  const selectedCompany = companies.find(
    (company) => company.id === selectedCompanyId,
  );
  const tripletexConnection = tripletexIntegration?.connection || undefined;
  const tripletexIsAvailable =
    tripletexIntegration !== null &&
    tripletexIntegration.availability !== "coming_soon";
  const isConnected = tripletexConnection?.status === "active";
  const needsReconnect =
    tripletexConnection?.status === "reauthorization_required" ||
    tripletexConnection?.status === "error" ||
    tripletexConnection?.status === "disconnected";

  const handleConnect = async (
    employeeToken: string,
    authorizationVersion: string,
  ) => {
    if (!selectedCompanyId || !tripletexIsAvailable || !tripletexIntegration) {
      return;
    }
    setCurrentAction("connect");
    setConnectError(null);
    setPageMessage(null);
    try {
      const connection = await connectTripletex(token, workspaceId, {
        companyId: selectedCompanyId,
        employeeToken,
        providerEnvironment: tripletexIntegration.providerEnvironment,
        authorizationConfirmed: true,
        authorizationVersion,
      });
      setTripletexIntegration((current) =>
        current
          ? { ...current, availability: "connected", connection }
          : current,
      );
      setConnectDialogOpen(false);

      try {
        const configured = await configureTripletexWebhooks(
          token,
          workspaceId,
          connection.id,
        );
        setTripletexIntegration((current) =>
          current ? { ...current, connection: configured } : current,
        );
        setPageMessage({
          variant: "success",
          text: t("workspace:integrations.tripletex.connected_success"),
        });
      } catch (webhookError) {
        setPageMessage({
          variant: "warning",
          text: getErrorMessage(
            webhookError,
            t("workspace:integrations.tripletex.webhook_setup_warning"),
          ),
        });
        await loadIntegration();
      }
    } catch (error) {
      setConnectError(
        getErrorMessage(
          error,
          t("workspace:integrations.errors.connect_failed"),
        ),
      );
    } finally {
      setCurrentAction(null);
    }
  };

  const handleTest = async () => {
    if (!tripletexConnection) return;
    setCurrentAction("test");
    setPageMessage(null);
    try {
      const tested = await testAccountingConnection(
        token,
        workspaceId,
        tripletexConnection.id,
      );
      setTripletexIntegration((current) =>
        current ? { ...current, connection: tested } : current,
      );
      setPageMessage({
        variant: "success",
        text: t("workspace:integrations.tripletex.test_success"),
      });
    } catch (error) {
      setPageMessage({
        variant: "error",
        text: getErrorMessage(
          error,
          t("workspace:integrations.errors.test_failed"),
        ),
      });
      await loadIntegration();
    } finally {
      setCurrentAction(null);
    }
  };

  const handleConfigureWebhooks = async () => {
    if (!tripletexConnection) return;
    setCurrentAction("webhooks");
    setPageMessage(null);
    try {
      const configured = await configureTripletexWebhooks(
        token,
        workspaceId,
        tripletexConnection.id,
      );
      setTripletexIntegration((current) =>
        current ? { ...current, connection: configured } : current,
      );
      setPageMessage({
        variant: "success",
        text: t("workspace:integrations.tripletex.webhook_success"),
      });
    } catch (error) {
      setPageMessage({
        variant: "error",
        text: getErrorMessage(
          error,
          t("workspace:integrations.errors.webhook_failed"),
        ),
      });
      await loadIntegration();
    } finally {
      setCurrentAction(null);
    }
  };

  const handleDisconnect = async () => {
    if (!tripletexConnection) return;
    setCurrentAction("disconnect");
    setPageMessage(null);
    try {
      const disconnected = await disconnectAccountingConnection(
        token,
        workspaceId,
        tripletexConnection.id,
      );
      setTripletexIntegration((current) =>
        current
          ? {
              ...current,
              availability:
                current.providerEnvironment === "production"
                  ? "pilot"
                  : "available",
              connection: disconnected,
            }
          : current,
      );
      setDisconnectDialogOpen(false);
      setPageMessage({
        variant: "success",
        text: t("workspace:integrations.tripletex.disconnected_success"),
      });
    } catch (error) {
      setPageMessage({
        variant: "error",
        text: getErrorMessage(
          error,
          t("workspace:integrations.errors.disconnect_failed"),
        ),
      });
    } finally {
      setCurrentAction(null);
    }
  };

  const webhookIsActive = tripletexConnection?.webhookStatus === "active";
  const tripletex = integrationCatalog.tripletex;
  const mcp = integrationCatalog["logibud-mcp"];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text sm:text-2xl">
            {detailMode ? "Tripletex" : t("workspace:integrations.title")}
          </h2>
          <p className="max-w-2xl pt-2 text-base text-secondary">
            {t(
              detailMode
                ? "workspace:integrations.tripletex.description"
                : "workspace:integrations.description",
            )}
          </p>
        </div>

        <div className="w-full sm:w-72">
          <Label
            htmlFor="integration-company"
            value={t("workspace:integrations.company_label")}
          />
          <select
            id="integration-company"
            value={selectedCompanyId}
            onChange={(event) => {
              setTripletexIntegration(null);
              setSelectedCompanyId(event.target.value);
              setPageMessage(null);
            }}
            disabled={companiesLoading || companies.length === 0}
            className={cn("mt-1 w-full", inputSurface)}
          >
            {companies.length === 0 ? (
              <option value="">
                {t("workspace:integrations.no_companies")}
              </option>
            ) : null}
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!canManage ? (
        <InfoMessageAtom variant="neutral">
          {t("workspace:integrations.manage_access_required")}
        </InfoMessageAtom>
      ) : null}

      {pageMessage ? (
        <InfoMessageAtom
          variant={pageMessage.variant}
          dismissible
          onDismiss={() => setPageMessage(null)}
        >
          {pageMessage.text}
        </InfoMessageAtom>
      ) : null}

      <div
        role="list"
        className={`grid grid-cols-1 items-start gap-3 ${
          detailMode ? "max-w-4xl" : "lg:grid-cols-2"
        }`}
        aria-label={t("workspace:integrations.title")}
      >
        <div
          role="listitem"
          className="overflow-hidden rounded-lg border border-border bg-surface"
        >
          <div className="flex flex-col gap-4 p-5">
            <div className="flex min-w-0 items-start gap-4">
              <IntegrationMark integration={tripletex} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-base font-semibold text-text">
                    {tripletex.name}
                  </h4>
                  <ConnectionStatus status={tripletexConnection?.status} />
                  {tripletexIntegration?.providerEnvironment === "test" ? (
                    <StatusBadge
                      status={t("workspace:integrations.test_environment")}
                      bgStyle="bg-brand-muted text-brand"
                      rounded
                    />
                  ) : null}
                  {tripletexIntegration?.availability === "pilot" ? (
                    <StatusBadge
                      status={t("workspace:integrations.pilot")}
                      bgStyle="bg-brand-muted text-brand"
                      rounded
                    />
                  ) : null}
                </div>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-secondary">
                  {t("workspace:integrations.tripletex.description")}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 sm:pl-16">
              {!tripletexIsAvailable ? (
                <Button variant="secondary" disabled>
                  {t("workspace:integrations.coming_soon")}
                </Button>
              ) : !isConnected ? (
                <Button
                  variant="primary"
                  disabled={
                    !canManage || !selectedCompanyId || currentAction !== null
                  }
                  onClick={() => {
                    setConnectError(null);
                    setConnectDialogOpen(true);
                  }}
                >
                  {t(
                    needsReconnect
                      ? "workspace:integrations.actions.reconnect"
                      : "workspace:integrations.actions.connect",
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    icon={<HiOutlineRefresh className="h-4 w-4" />}
                    disabled={!canManage || currentAction !== null}
                    onClick={handleTest}
                  >
                    {currentAction === "test"
                      ? t("workspace:integrations.actions.testing")
                      : t("workspace:integrations.actions.test")}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={!canManage || currentAction !== null}
                    onClick={() => setDisconnectDialogOpen(true)}
                  >
                    {t("workspace:integrations.actions.disconnect")}
                  </Button>
                </>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 border-t border-border px-5 py-4 text-sm text-secondary">
              <Spinner size="sm" />
              {t("workspace:integrations.loading")}
            </div>
          ) : tripletexConnection ? (
            <div className="px-5">
              <IntegrationRow
                label={t("workspace:integrations.tripletex.tripletex_company")}
                value={
                  tripletexConnection.externalCompanyName ||
                  selectedCompany?.name ||
                  "-"
                }
              />
              <IntegrationRow
                label={t("workspace:integrations.tripletex.connection")}
                value={
                  <span className="inline-flex items-center justify-end gap-2">
                    {isConnected ? (
                      <HiCheckCircle className="h-5 w-5 text-lgb-green-400" />
                    ) : (
                      <HiExclamationCircle className="h-5 w-5 text-yellow-500" />
                    )}
                    {t(
                      isConnected
                        ? "workspace:integrations.tripletex.api_connected"
                        : "workspace:integrations.tripletex.api_not_connected",
                    )}
                  </span>
                }
              />
              <IntegrationRow
                label={t("workspace:integrations.tripletex.automatic_updates")}
                value={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <span className="inline-flex items-center gap-2">
                      {webhookIsActive ? (
                        <HiCheckCircle className="h-5 w-5 text-lgb-green-400" />
                      ) : (
                        <HiExclamationCircle className="h-5 w-5 text-yellow-500" />
                      )}
                      {t(
                        webhookIsActive
                          ? "workspace:integrations.tripletex.updates_active"
                          : "workspace:integrations.tripletex.updates_inactive",
                      )}
                    </span>
                    {isConnected && !webhookIsActive ? (
                      <button
                        type="button"
                        className="font-medium text-brand hover:underline disabled:opacity-50"
                        disabled={!canManage || currentAction !== null}
                        onClick={handleConfigureWebhooks}
                      >
                        {currentAction === "webhooks"
                          ? t("workspace:integrations.actions.retrying")
                          : t("workspace:integrations.actions.retry")}
                      </button>
                    ) : null}
                  </div>
                }
              />
              {tripletexConnection.lastVerifiedAt ? (
                <IntegrationRow
                  label={t("workspace:integrations.tripletex.last_verified")}
                  value={
                    formatDateTime(tripletexConnection.lastVerifiedAt) || "-"
                  }
                />
              ) : null}
              {tripletexConnection.lastBackgroundSyncAt ? (
                <IntegrationRow
                  label={t(
                    "workspace:integrations.tripletex.last_automatic_sync",
                  )}
                  value={
                    <BackgroundSyncStatus
                      status={tripletexConnection.lastBackgroundSyncStatus}
                      timestamp={tripletexConnection.lastBackgroundSyncAt}
                    />
                  }
                />
              ) : null}
              {tripletexConnection.lastErrorMessage ? (
                <IntegrationRow
                  label={t("workspace:integrations.tripletex.last_error")}
                  value={
                    <span className="text-red-600 dark:text-red-300">
                      {tripletexConnection.lastErrorMessage}
                    </span>
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {!detailMode ? (
          <div
            role="listitem"
            className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5"
          >
            <div className="flex min-w-0 items-start gap-4">
              <IntegrationMark integration={mcp} />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-base font-semibold text-text">
                    {mcp.name}
                  </h4>
                  <StatusBadge
                    status={t("workspace:integrations.ai.active_access", {
                      count: activeMcpTokenCount,
                    })}
                    bgStyle={
                      activeMcpTokenCount > 0
                        ? "bg-lgb-green-100 text-lgb-green-600 dark:bg-lgb-green-700/20 dark:text-lgb-green-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    }
                    rounded
                  />
                </div>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-secondary">
                  {t("workspace:integrations.ai.mcp_description")}
                </p>
              </div>
            </div>
            <div className="flex justify-end sm:pl-16">
              <Button
                variant="secondary"
                onClick={() => setSearchParams({ tab: "mcp-tokens" })}
              >
                {t("workspace:integrations.actions.manage_access")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <TripletexConnectDialog
        show={connectDialogOpen}
        companyName={selectedCompany?.name || ""}
        isReconnect={Boolean(tripletexConnection)}
        isSubmitting={currentAction === "connect"}
        errorMessage={connectError}
        authorizationVersion={tripletexIntegration?.authorizationVersion || ""}
        onClose={() => {
          if (currentAction !== "connect") {
            setConnectDialogOpen(false);
            setConnectError(null);
          }
        }}
        onConnect={handleConnect}
      />

      <Dialog
        show={disconnectDialogOpen}
        size="md"
        title={t("workspace:integrations.tripletex.disconnect_title")}
        toggleModal={() => {
          if (currentAction !== "disconnect") setDisconnectDialogOpen(false);
        }}
        isLoading={currentAction === "disconnect"}
        confirmButtonColor="failure"
        buttonConfirmText={t("workspace:integrations.actions.disconnect")}
        buttonCloseText={t("common:cancel")}
        confirmButton={handleDisconnect}
        content={
          <p className="text-sm leading-6 text-secondary">
            {t("workspace:integrations.tripletex.disconnect_description", {
              company: selectedCompany?.name || "",
            })}
          </p>
        }
      />
    </div>
  );
};

export default IntegrationsSettingsContent;
